import type { InvitationWithWorkspace } from "@repo/share-log-interfaces";
import type { StateStatus, StoreDeps } from "../types";
import type { ImmerSet } from "../helpers/create-store";
import { createStore } from "../helpers/create-store";
import { withFetchStatus } from "../helpers/with-fetch-status";
import { createReset } from "../helpers/create-reset";

interface InvitationState {
  invitations: InvitationWithWorkspace[];
  status: StateStatus;
}

interface InvitationActions {
  fetchInvitations: () => Promise<void>;
  createInvitation: (workspaceId: number, email: string) => Promise<void>;
  acceptInvitation: (id: number) => Promise<void>;
  rejectInvitation: (id: number) => Promise<void>;
  reset: () => void;
}

export type InvitationStore = InvitationState & InvitationActions;

const initialState: InvitationState = {
  invitations: [],
  status: "idle",
};

export function createInvitationStore(deps: StoreDeps) {
  return createStore<InvitationStore>("InvitationStore", (set) => ({
    ...initialState,

    fetchInvitations: () =>
      withFetchStatus(
        (s) => set((state) => { state.status = s; }),
        deps.logger,
        async () => {
          const invitations = await deps.api.invitation.listMyInvitations();
          set((state) => { state.invitations = invitations; });
        },
      ),

    createInvitation: async (workspaceId, email) => {
      await deps.api.invitation.create(workspaceId, { inviteeEmail: email });
    },

    acceptInvitation: async (id) => {
      await deps.api.invitation.accept(id);
      set((state) => {
        state.invitations = state.invitations.filter((i) => i.id !== id);
      });
    },

    rejectInvitation: async (id) => {
      await deps.api.invitation.reject(id);
      set((state) => {
        state.invitations = state.invitations.filter((i) => i.id !== id);
      });
    },

    reset: createReset(set as ImmerSet<InvitationState>, initialState),
  }));
}
